import React, { useCallback, useEffect, useState } from 'react';
import { approvePath, buildGame, buildMission, chooseBackground, copyPlan, makePath, chooseItemPicture, deletePlan, drawItems, drawViews, getBackgrounds, swapItemPictures, getItemPictures, getPlan,
  getViews, listPlans, redrawItem, saveLayout, saveViews } from '../api/gameBuilderAPI';
import InventoryCatalog from './InventoryCatalog';
import MissionFlow from './MissionFlow';
import PathEditor from './PathEditor';
import PublishPanel from './PublishPanel';
import NewPlan from './NewPlan';
import SceneArranger from './SceneArranger';
import AddMission from './AddMission';
import ScenePreview from './ScenePreview';
import ChainCard from './ChainCard';
import './GameBuilder.css';

/**
 * The Game Builder - the Studio tab of the workspace (pages/StudioWorkspace.js); docs/HOW_IT_WORKS.md. Plans are the org's:
 * a creator sees their own, an owner every creator's. `data-tour` marks what the walkthroughs point at (reacttour/StudioTours.js).
 * Plans on the left; the selected plan's missions on the right. Each mission is a WORKFLOW of three stages,
 *   Design › Build › Share (STAGES below), as a breadcrumb: a stage opens once it's reached (done stages show ✓), and the
 *   card opens on the mission's next one. The ONE call to action follows the mission's status - Design with AI, then the
 *   design's own Approve (which also paints the scene), then Publish - and a done stage has none: it shows what was made,
 *   with quiet links to change it (edit or redesign, arrange or repaint). One mission is open at a time (the others are a
 *   line each); saving a new plan starts designing its first mission, and a design waiting for review opens by itself.
 * Games are always linked: each built game joins the end of the plan's chain (ChainCard - backend plan.chain_games);
 * the creator picks each link - later, or another room. A move to another room plays on the floor plan, where the
 * player chooses the room (SitePlayer); otherwise the chain plays straight through (StorylinePlayer).
 * Slow steps run as background jobs on the server (api/gameBuilderAPI.js untilDone).
 */
const WORKING = {
  path: { queued: 'Starting…', running: 'Working out how the simulation is finished… (about 20 seconds)' },
  approve: { queued: 'Starting…', running: 'Writing the words and building it… (about 20 seconds), then the scene is painted' },
  mission: { queued: 'Starting…', running: 'Writing the mission… (about 20 seconds)' },
  game: { queued: 'Starting…', running: 'Painting the scene… (up to a minute)' }
};

const statusOf = (s) => (s?.built && s?.stale?.length ? { key: s.game ? 'game' : 'built', label: 'Needs rebuild', attention: true }
  : s?.game && s?.issues ? { key: 'game', label: 'Needs attention', attention: true }
  : s?.game && s?.published ? { key: 'shared', label: 'Shared' }
  : s?.game ? { key: 'game', label: 'Ready to share' } : s?.built ? { key: 'built', label: 'Ready to paint' }
    : s?.path ? { key: 'path', label: 'Design to review' } : { key: 'new', label: 'Not started' });
const LEVEL_ICON = { ok: '✅', warn: '⚠️', error: '⛔' };

// A mission's workflow: its stages, and the furthest one each status (statusOf's key) has reached - where its card opens.
const STAGES = [{ key: 'design', label: 'Design' }, { key: 'build', label: 'Scene' }, { key: 'share', label: 'Share' }];
const REACHED = { new: 0, path: 0, built: 1, game: 2, shared: 2 };
const dateOf = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '');

// canBrowseItems: the platform admin - the item inventory is theirs only (pages/tabs.js `adminOnly`)
export default function GameBuilder({ startCreating = false, onCreatingChange, request, headerExtra = null, canBrowseItems = false }) {
  const [plans, setPlans] = useState(null);
  const [selected, setSelected] = useState(null);
  const [plan, setPlan] = useState(null);
  const [pageError, setPageError] = useState('');
  const [creating, setCreatingState] = useState(startCreating);
  const setCreating = (value) => { setCreatingState(value); onCreatingChange?.(value); };
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [work, setWork] = useState({});         // mission id -> { step: 'mission'|'game', phase }
  const [errors, setErrors] = useState({});     // mission id -> message
  const [briefs, setBriefs] = useState({});     // mission id -> built mission (for details)
  const [open, setOpen] = useState({});         // mission id -> details shown
  const [specs, setSpecs] = useState({});       // mission id -> game spec
  const [catalog, setCatalog] = useState(false);  // the item inventory page
  const [paths, setPaths] = useState({});         // mission id -> its winning path (as the review screen shows it)
  const [stepOf, setStepOf] = useState({});       // mission id -> the stage open on its card (none: the one it has reached)
  const [focus, setFocus] = useState(null);       // the mission open on the page (the others are a line each)
  const [designFirst, setDesignFirst] = useState(null);   // a plan just saved: design its first mission straight away
  const goStep = (missionId, key) => setStepOf((s) => ({ ...s, [missionId]: key }));
  // A mission's game plays in its own window (/game-builder/play - GamePages.jsx), opened right on the click so the
  // browser doesn't block it; that page builds the game first if it isn't built yet.
  // ...and so do the plan's storyline and its site map
  const openPlan = (what) => window.open(`/game-builder/${what}/${encodeURIComponent(plan.id)}`, '_blank', 'noopener');
  const openPlay = (missionId) => window.open(`/game-builder/play/${encodeURIComponent(plan.id)}/${encodeURIComponent(missionId)}`, '_blank', 'noopener');
  const [arranging, setArranging] = useState(null);
  const [arrangeItem, setArrangeItem] = useState(null);   // the item the arranger opens on (clicked in the preview)

  const loadPlans = useCallback(async (select) => {
    try {
      const data = await listPlans();
      setPlans(data.plans);
      setSelected((cur) => select || cur || data.plans[0]?.id || null);
    } catch (e) {
      setPageError(e.message);
    }
  }, []);

  useEffect(() => { loadPlans(); }, [loadPlans]);
  // The workspace asks for a plan (a dashboard card, the search) or a new one (Create) - pages/StudioWorkspace.js.
  useEffect(() => {
    if (request?.plan) { setCreatingState(false); setSelected(request.plan); } else if (request?.create) setCreatingState(true);
  }, [request]);

  useEffect(() => {
    if (!selected) { setPlan(null); return; }
    setPlan(null); setBriefs({}); setSpecs({}); setOpen({}); setErrors({}); setConfirmDelete(false); setPaths({}); setStepOf({}); setFocus(null);
    getPlan(selected).then(setPlan).catch((e) => setPageError(e.message));
  }, [selected]);

  const setStatus = (missionId, patch) =>
    setPlan((p) => ({ ...p, status: { ...p.status, [missionId]: { ...p.status?.[missionId], ...patch } } }));

  const run = async (missionId, step, fn) => {
    setErrors((e) => ({ ...e, [missionId]: '' }));
    setWork((w) => ({ ...w, [missionId]: { step, phase: 'queued' } }));
    try {
      return await fn((phase) => setWork((w) => ({ ...w, [missionId]: { step, phase } })));
    } catch (e) {
      setErrors((x) => ({ ...x, [missionId]: e.message }));
      return null;
    } finally {
      setWork((w) => ({ ...w, [missionId]: null }));
    }
  };

  // A new mission: the AI works out how the game is won (its path); the creator reviews it before anything is built.
  // `fresh` throws the saved mission (and path) away and asks again - a new AI answer.
  const planMission = async (m, fresh = false) => {
    setFocus(m.id);
    const view = await run(m.id, 'path', (p) => makePath(plan.id, m.id, p, fresh));
    if (!view) return;
    setPaths((x) => ({ ...x, [m.id]: { ...view, made: Date.now() } }));     // `made`: a new path opens a new editor
    if (fresh) {
      setBriefs((b) => { const next = { ...b }; delete next[m.id]; return next; });
      setSpecs((x) => { const next = { ...x }; delete next[m.id]; return next; });
      setStatus(m.id, { built: false, game: false, issues: 0, path: true });
    } else setStatus(m.id, { path: true });
    setOpen((o) => ({ ...o, [m.id]: false }));
    goStep(m.id, 'design');
  };
  // A designed mission's saved design, to look at or change - no AI, so no progress line
  const loadDesign = async (m) => {
    try {
      const view = await makePath(plan.id, m.id, () => {}, false);
      setPaths((x) => ({ ...x, [m.id]: { ...view, made: Date.now() } }));
    } catch (e) {
      setErrors((x) => ({ ...x, [m.id]: e.message }));
    }
  };
  // A design waiting for review opens by itself (the saved one - no AI); a plan just saved starts on its first mission.
  useEffect(() => {
    if (!plan) return;
    if (designFirst === plan.id) {
      setDesignFirst(null);
      const first = plan.missions[0];
      if (first && !plan.status?.[first.id]?.path) planMission(first);
    }
  }, [plan?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  // The open mission's design, when it waits for review, opens by itself (the saved one - no AI)
  const focusedId = plan && (focus || (plan.missions.find((m) => !plan.status?.[m.id]?.game) || plan.missions[0])?.id);
  useEffect(() => {
    const m = plan?.missions.find((x) => x.id === focusedId);
    if (m && plan.status?.[m.id]?.path && !plan.status?.[m.id]?.built && !paths[m.id]) loadDesign(m);
  }, [plan?.id, focusedId]);   // eslint-disable-line react-hooks/exhaustive-deps

  // The scene, painted now (a room already in the library: seconds, no image call) - then the card moves on to Share, or
  // stays on Build when something in it needs fixing first.
  const paint = async (m) => {
    const spec = await run(m.id, 'game', (p) => buildGame(plan.id, m.id, p));
    if (!spec) return null;
    const issues = issuesOf(spec);
    setSpecs((x) => ({ ...x, [m.id]: spec })); setStatus(m.id, { game: true, issues });
    goStep(m.id, issues ? 'build' : 'share');
    getPlan(plan.id).then((p) => setPlan((cur) => ({ ...cur, storyline: p.storyline }))).catch(() => {});   // it joined the chain
    return spec;
  };
  // Approved: the words are written (one AI call), code builds the mission from the path - and the scene is painted
  // straight after, so approving is the last click before Share.
  const buildFromPath = async (m) => {
    const brief = await run(m.id, 'approve', (p) => approvePath(plan.id, m.id, p));
    if (!brief) return;
    setBriefs((b) => ({ ...b, [m.id]: brief }));
    setSpecs((x) => { const next = { ...x }; delete next[m.id]; return next; });
    setStatus(m.id, brief.needs_new_pictures ? { built: true, game: false, issues: 0, stale: [] } : { built: true, stale: [] });
    setPaths((x) => ({ ...x, [m.id]: { ...x[m.id], approved: true } }));
    goStep(m.id, 'build');
    await paint(m);
  };

  // The painted scene, to look at or arrange (built: comes straight back)
  const ensureSpec = async (m) => {
    if (specs[m.id]) return specs[m.id];
    const spec = await run(m.id, 'game', (p) => buildGame(plan.id, m.id, p));
    if (spec) { setSpecs((s) => ({ ...s, [m.id]: spec })); setStatus(m.id, { game: true, issues: issuesOf(spec) }); }
    return spec;
  };
  // Throw the saved mission away and build it again - a fresh AI answer, not the cached one (one AI
  // call); its game is discarded too, and is built again with new art.
  const rebuildMission = async (m) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Redesign "${m.name}" with AI?\n\nThe AI designs it afresh (one AI call) for you to review. `
      + 'Its current design and simulation are replaced; paint the scene again afterwards (two image calls).')) return;
    await planMission(m, true);
  };
  const issuesOf = (spec) => (spec.checklist || []).filter((c) => c.level === 'error').length;
  // New art for the same mission: a fresh background and a fresh item sheet (two image calls). The
  // layout stays; picture choices reset (the pictures are new).
  const rebuildGame = async (m) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Repaint everything in "${m.name}"?\n\nNew pictures and a new background (two image calls). `
      + 'The mission and your layout stay; picture choices are reset.')) return;
    const spec = await run(m.id, 'game', (p) => buildGame(plan.id, m.id, p, ['background', 'pictures']));
    if (spec) { setSpecs((s) => ({ ...s, [m.id]: spec })); setStatus(m.id, { game: true, issues: issuesOf(spec) }); }
  };
  // Fresh backgrounds (one image call: four versions of the room, the first used) - the old one painted the items, or
  // predates that rule.
  const redoBackground = async (missionId, looks = '') => {
    const spec = await run(missionId, 'game', (p) => buildGame(plan.id, missionId, p, ['background'], looks));
    if (spec) { setSpecs((s) => ({ ...s, [missionId]: spec })); setStatus(missionId, { game: true, issues: issuesOf(spec) }); }
    return spec;
  };

  const toggleDetails = async (m) => {
    if (open[m.id]) { setOpen((o) => ({ ...o, [m.id]: false })); return; }
    if (!briefs[m.id]) {
      const brief = await run(m.id, 'mission', (p) => buildMission(plan.id, m.id, p));   // saved: instant
      if (!brief) return;
      setBriefs((b) => ({ ...b, [m.id]: brief }));
    }
    setOpen((o) => ({ ...o, [m.id]: true }));
  };

  const removePlan = async () => {
    try {
      await deletePlan(plan.id);
      setConfirmDelete(false);
      setSelected(null);
      loadPlans();
    } catch (e) {
      setPageError(e.message);
    }
  };

  const duplicatePlan = async () => {
    try {
      const { id } = await copyPlan(plan.id);
      loadPlans(id);
    } catch (e) {
      setPageError(e.message);
    }
  };

  // One name per action, everywhere it's offered (a stage's call to action, a link inside a stage, the checklist).
  const arrange = async (m, item = null) => { const spec = await ensureSpec(m); if (spec) { setArrangeItem(item); setArranging(m.id); } };
  const playTest = (m) => openPlay(m.id);
  // Opening a done stage shows what was made there (all saved - no AI, no images): the design, the painted game's checklist.
  const openStep = (m, key) => {
    goStep(m.id, key);
    const s = plan.status?.[m.id] || {};
    if (key === 'design' && s.built && !paths[m.id]) loadDesign(m);
    if (key === 'build' && s.game && !specs[m.id]) ensureSpec(m);
  };
  const repaintBackground = (m) => {
    // eslint-disable-next-line no-alert
    if (window.confirm(`Repaint the background of "${m.name}"? One image call draws four versions (the first is used - `
      + 'pick another under "Background…" in Arrange the scene); the pictures and layout stay.')) redoBackground(m.id);
  };

  // The mission open on the page: the one asked for, else the first with work left (else the first)
  const focused = focusedId;
  const missionCard = (m) => {
    const st = statusOf(plan.status?.[m.id]);
    const s = plan.status?.[m.id] || {};
    const busy = work[m.id];
    if (m.id !== focused) {
      return (
        <section key={m.id} className="gb-card gb-mission is-folded">
          <h3 className="gb-mission-line">
            <button type="button" aria-expanded="false" onClick={() => setFocus(m.id)}>
              <span className="gb-mid">{m.id}</span>
              <span className="gb-mission-name">{m.name}</span>
              <span className={`gb-status gb-status-${st.key} ${st.attention ? 'gb-status-attention' : ''}`}>{st.label}</span>
            </button>
          </h3>
          {busy && <p className="gb-progress gb-small" role="status"><span className="gb-spinner" /> {WORKING[busy.step][busy.phase] || WORKING[busy.step].running}</p>}
        </section>
      );
    }
    const reached = REACHED[st.key];
    const asked = STAGES.findIndex((x) => x.key === stepOf[m.id]);
    const current = asked >= 0 && asked <= reached ? stepOf[m.id] : STAGES[reached].key;
    const done = { design: !!s.built, build: !!s.game, share: false };
    // what the stage is for, and its ONE call to action - from the mission's status; a done stage has none
    const { note, cta } = {
      design: st.key === 'new' ? { note: 'The AI reads the plan and designs what is in the room and what the player must do - about 10 seconds.',
        cta: { label: 'Design with AI', go: () => planMission(m) } }
        : !s.built ? { note: 'This is what the player will find and do. If it looks right, build it - or edit it first.',
          cta: paths[m.id] || !errors[m.id] ? null : { label: 'Open the design', go: () => loadDesign(m) } }   // the design's own Approve
          : { note: 'What the player finds and must do. Edit it and rebuild to change the simulation.' },
      build: !s.game ? { note: 'The room and its things are painted - up to a minute.',
        cta: { label: 'Paint the scene', go: () => paint(m) } }
        : { note: s.issues ? 'Painted - but something in it needs fixing before you share it (below).'
          : 'Painted. Click the scene, or one of its things, to move things around or choose their pictures.',
          cta: s.issues ? { label: 'Fix in the scene', go: () => arrange(m) } : null },
      share: { note: s.published ? 'Shared - learners play the link in the browser, no account needed.'
        : 'Publish it to get a link - learners play in the browser, no account needed. Play it yourself first if you like.' },
    }[current];
    return (
      <section key={m.id} className="gb-card gb-mission" data-tour={`gb-mission-${st.key}`}>
        <header className="gb-mission-head">
          <span className="gb-mid">{m.id}</span>
          <h3>{m.name}</h3>
          <span className={`gb-status gb-status-${st.key} ${st.attention ? 'gb-status-attention' : ''}`} data-tour="gb-status">{st.label}</span>
        </header>
        <p>{m.objective}</p>
        <p className="gb-muted gb-small">{m.scenario}</p>

        <nav className="gb-steps" aria-label={`Stages of ${m.name}`} data-tour="gb-steps">
          <ol>
            {STAGES.map((x, k) => {
              const open = k <= reached;
              return (
                <li key={x.key} className={`gb-step ${x.key === current ? 'is-current' : ''} ${done[x.key] ? 'is-done' : ''} ${open ? '' : 'is-locked'}`}>
                  <button type="button" disabled={!open || !!busy} aria-current={x.key === current ? 'step' : undefined}
                    title={open ? undefined : 'Finish the stage before this one first'} onClick={() => openStep(m, x.key)}>
                    <span className="gb-step-n" aria-hidden="true">{done[x.key] ? '✓' : k + 1}</span>{x.label}
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>

        <div className="gb-step-body" data-step={current}>
          <p className="gb-muted gb-small">{note}</p>
          {current === 'build' && specs[m.id]?.checklist?.some((c) => c.level !== 'ok') && (
            <ul className="gb-checklist">
              {specs[m.id].checklist.filter((c) => c.level !== 'ok').map((c) => (
                <li key={`${c.id}-${c.text}`} className={`is-${c.level}`}>{LEVEL_ICON[c.level]} {c.text}
                  {c.action === 'redo_background' && !busy && (
                    <button type="button" className="gb-link" onClick={() => repaintBackground(m)}>Repaint the background</button>
                  )}
                  {c.action === 'rebuild_mission' && !busy && (
                    <button type="button" className="gb-link" onClick={() => rebuildMission(m)}>Redesign with AI</button>
                  )}
                </li>
              ))}
            </ul>
          )}
          <div className="gb-actions">
            {busy ? (
              <span className="gb-progress" role="status"><span className="gb-spinner" /> {WORKING[busy.step][busy.phase] || WORKING[busy.step].running}</span>
            ) : (
              <>
                {cta && <button type="button" className="gb-btn" data-tour="gb-next" onClick={cta.go}>{cta.label}</button>}
                {current === 'design' && s.built && (
                  <>
                    <button type="button" className="gb-link" onClick={() => toggleDetails(m)}>{open[m.id] ? 'Hide how it plays' : 'How it plays'}</button>
                    <button type="button" className="gb-link" onClick={() => rebuildMission(m)}>Redesign with AI</button>
                  </>
                )}
                {current === 'build' && s.game && (
                  <>
                    <button type="button" className="gb-link" onClick={() => rebuildGame(m)}>Repaint everything</button>
                    <button type="button" className="gb-link" onClick={() => repaintBackground(m)}>Repaint only the background</button>
                  </>
                )}
                {current === 'share' && (
                  <button type="button" className="gb-btn gb-btn-quiet" onClick={() => playTest(m)}>▶ Play-test</button>
                )}
              </>
            )}
          </div>
          {errors[m.id] && (
            <div className="gb-error" role="alert">
              <span>{errors[m.id]}</span>
              {cta && <button type="button" className="gb-btn gb-btn-sm gb-btn-quiet" onClick={cta.go}>Try again</button>}
            </div>
          )}
          {current === 'build' && s.game && specs[m.id] && !busy && <ScenePreview spec={specs[m.id]} onOpen={(item) => arrange(m, item)} />}
          {current === 'design' && open[m.id] && briefs[m.id] && <MissionFlow brief={briefs[m.id]} />}
          {current === 'design' && paths[m.id] && (
            <PathEditor key={`${m.id}-${paths[m.id].made}`} planId={plan.id} mission={m} view={paths[m.id]} busy={!!busy}
              onChange={(view) => setPaths((x) => ({ ...x, [m.id]: { ...view, made: x[m.id]?.made } }))}
              onApprove={() => buildFromPath(m)} />
          )}
          {current === 'share' && specs[m.id] && !busy && <ScenePreview spec={specs[m.id]} onOpen={(item) => arrange(m, item)} />}
          {current === 'share' && <PublishPanel planId={plan.id} missionId={m.id} onPublished={(on) => setStatus(m.id, { published: on })} />}
        </div>
      </section>
    );
  };

  return (
    <div className="gb-page">
      <header className="gb-header">
        <div>
          <h1>Studio</h1>
          <p className="gb-muted">Turn a procedure into a hands-on training simulation: players spot what's wrong and put it right.</p>
        </div>
        <div className="gb-header-actions">
          {headerExtra}
          {canBrowseItems && !creating && !catalog && <button type="button" className="gb-btn gb-btn-quiet" data-tour="gb-inventory" onClick={() => setCatalog(true)}>🧰 Item inventory</button>}
          {!creating && !catalog && <button type="button" className="gb-btn" data-tour="gb-new-plan" onClick={() => setCreating(true)}>＋ New plan</button>}
        </div>
      </header>

      {pageError && (
        <div className="gb-error" role="alert">
          <span>{pageError}</span>
          <button type="button" className="gb-btn gb-btn-sm gb-btn-quiet" onClick={() => { setPageError(''); loadPlans(); }}>Try again</button>
        </div>
      )}

      {catalog && <InventoryCatalog onClose={() => setCatalog(false)} />}
      {creating && !catalog && (
        <NewPlan onCancel={() => setCreating(false)} onSaved={(id) => { setCreating(false); setDesignFirst(id); loadPlans(id); }} />
      )}

      {!creating && !catalog && plans && plans.length === 0 && (
        <section className="gb-card gb-empty" data-tour="gb-empty">
          <h2>No plans yet</h2>
          <p className="gb-muted">Start from a document (an SOP, manual or policy), or just describe the job in a short chat.</p>
          <button type="button" className="gb-btn" onClick={() => setCreating(true)}>＋ New plan</button>
        </section>
      )}

      {!creating && !catalog && plans === null && !pageError && <p className="gb-muted"><span className="gb-spinner" /> Loading plans…</p>}

      {!creating && !catalog && plans && plans.length > 0 && (
        <div className="gb-layout">
          <nav className="gb-plans" aria-label="Plans" data-tour="gb-plans">
            <h2>Plans</h2>
            {plans.map((p) => (
              <button key={p.id} type="button" className={`gb-plan-pick ${p.id === selected ? 'is-active' : ''}`}
                aria-current={p.id === selected ? 'true' : undefined} onClick={() => setSelected(p.id)}>
                <strong>{p.goal || p.id}</strong>
                <span className="gb-muted gb-small">{p.missions} mission{p.missions === 1 ? '' : 's'}{p.created_at ? ` · ${dateOf(p.created_at)}` : ''}
                  {p.created_by ? ` · ${p.created_by.split('@')[0]}` : ''}</span>
              </button>
            ))}
          </nav>

          <main>
            {!plan && <p className="gb-muted"><span className="gb-spinner" /> Loading the plan…</p>}
            {plan && (
              <>
                <section className="gb-card" data-tour="gb-plan">
                  <div className="gb-plan-head">
                    <h2>{plan.goal}</h2>
                    {!confirmDelete && (
                      <span className="gb-plan-actions">
                        <button type="button" className="gb-btn gb-btn-sm gb-btn-quiet" title="A copy to change - its missions come along, its links don't"
                          onClick={duplicatePlan}>Duplicate</button>
                        <button type="button" className="gb-btn gb-btn-sm gb-btn-quiet" onClick={() => setConfirmDelete(true)}>Delete plan</button>
                      </span>
                    )}
                  </div>
                  {confirmDelete && (
                    <div className="gb-confirm" role="alert">
                      <span>Delete this plan, its missions and their simulations? This can't be undone.</span>
                      <button type="button" className="gb-btn gb-btn-sm gb-btn-danger" onClick={removePlan}>Delete</button>
                      <button type="button" className="gb-btn gb-btn-sm gb-btn-quiet" onClick={() => setConfirmDelete(false)}>Cancel</button>
                    </div>
                  )}
                  <dl className="gb-facts">
                    <div><dt>Player</dt><dd>{plan.player_role}</dd></div>
                    <div><dt>Who plays</dt><dd>{plan.learners}</dd></div>
                    {plan.other_roles?.length > 0 && <div><dt>Others in the scene</dt><dd>{plan.other_roles.join(', ')}</dd></div>}
                    {plan.document && <div><dt>Source</dt><dd>{plan.document}</dd></div>}
                  </dl>
                </section>
                {/* A move to another room plays on the floor plan (the player picks the room); else straight through. */}
                {plan.missions.filter((m) => plan.status?.[m.id]?.game).length >= 2 && (
                  <ChainCard plan={plan} onSaved={(patch) => setPlan((p) => ({ ...p, ...patch }))}
                    onPlay={() => openPlan('story')} />
                )}
                {plan.missions.map(missionCard)}
                <AddMission key={plan.id} plan={plan} onAdded={(next) => setPlan((p) => ({ ...p, ...next }))} />
              </>
            )}
          </main>
        </div>
      )}

      {arranging && specs[arranging] && (
        <SceneArranger
          spec={specs[arranging]} initialSelected={arrangeItem}
          onCancel={() => setArranging(null)}
          onRedoBackground={async (looks = '') => {
            // One image call: four versions of the room, the first used - the arranger stays open to pick (errors show there).
            const id = arranging;
            const spec = await buildGame(plan.id, id, null, ['background'], looks);
            setSpecs((s) => ({ ...s, [id]: spec }));
            setStatus(id, { game: true, issues: issuesOf(spec) });
          }}
          library={specs[arranging].assets?.room ? {
            // the shared library: other backgrounds of this room and other pictures of an item - free to choose
            backgrounds: () => getBackgrounds(plan.id, arranging),
            chooseBackground: async (variant) => {
              const spec = await chooseBackground(plan.id, arranging, variant);
              setSpecs((s) => ({ ...s, [arranging]: spec }));
            },
            pictures: (itemId) => getItemPictures(plan.id, arranging, itemId),
            choosePicture: async (itemId, ref, state = null) => {
              const spec = await chooseItemPicture(plan.id, arranging, itemId, ref, state);
              setSpecs((s) => ({ ...s, [arranging]: spec }));
            },
            swapPicture: async (itemId, other) => {
              const spec = await swapItemPictures(plan.id, arranging, itemId, other);
              setSpecs((s) => ({ ...s, [arranging]: spec }));
            },
            drawMany: async (itemIds, looks = {}) => {  // one image call for all of them (a sheet, cut apart); looks: {id: words}
              const spec = await drawItems(plan.id, arranging, itemIds, looks);
              setSpecs((s) => ({ ...s, [arranging]: spec }));
            },
            // views: more sections of the work area - pick from the library (free) or draw new ones (one image call for four)
            views: () => getViews(plan.id, arranging),
            saveViews: (choice) => saveViews(plan.id, arranging, choice),
            drawViews: (places, looks = '') => drawViews(plan.id, arranging, places, looks),
          } : null}
          onRedraw={async (itemId, looks) => {
            // One image call; the arranger stays open and shows the new picture (errors show in the arranger).
            const spec = await redrawItem(plan.id, arranging, itemId, looks);
            setSpecs((s) => ({ ...s, [arranging]: spec }));
            setStatus(arranging, { issues: issuesOf(spec) });
          }}
          onSave={async (positions, art, areas) => {
            await saveLayout(plan.id, arranging, positions, art, areas);
            // The game is built, so this comes straight back with the new positions (no new images).
            const spec = await buildGame(plan.id, arranging);
            setSpecs((s) => ({ ...s, [arranging]: spec }));
            setStatus(arranging, { arranged: true, issues: issuesOf(spec) });
            setArranging(null);
          }}
        />
      )}
    </div>
  );
}
